import * as angular from 'angular';

(function () {
    'use strict';

    /**
     * @ngdoc directive
     * @name pkpAutocompleteTextbox
     * @memberof app
     *
     * @restrict E
     * @scope {}
     *
     * @description
     * This directive is an autocomplete textbox. It lists checkboxes with suggested values to filter for.
     * The suggestions can be filtered again with a text field.
     *
     * An autocomplete textbox is a suggestion-based input field.
     *
     * The input is sent to the backend to provide suggestions to filter by. Suggestions will be provided
     * according to what the user has typed into the textbox. The user can then select from the suggestions or
     * not. If not, all the suggestions will be selected.
     *
     * If you have entered a search query but not selected any suggestion, that actually means you've selected all of
     * them.
     * If you remove the search query, all of your selected suggestions will also be removed and this is how you reset
     * the filter.
     *
     * @param {string} filterColumn The column to filter by from {@link app.COLUMN_FILTER#COL}. Is used in combination
     *  with data from filterTable to access the model to save the column filter selection to.
     *  Will prevent suggested values.
     *  @param {Object} currentFilter The currentFilter contains the value/values to filter the list by.
     *  This/These are sent to the backend in order to properly filter the list.
     *  @param {string} filterTable The filterTable the filterColumn belongs to {@Link app.COLUMN_FILTER#TABLE}.
     *  @param {string] defaultPlaceholder The placeholder is placed in the input field which is going to be
     *  autocompleted.
     * @author .btc
     */

    angular
        .module('app')
        .directive('pkpAutocompleteTextbox', pkpAutocompleteTextbox);

    function pkpAutocompleteTextbox(
        $timeout,
        ColumnFilter,
        columnFilterService
    ) {
        return {
            restrict: 'E',
            scope: {
                filterColumn: '=',
                currentFilter: '=',
                filterTable: '=',
                defaultPlaceholder: '@'
            },
            template:
                require('app/_directives/pkp-autocomplete-textbox.directive.html'),
            link: linkFn
        };

        function linkFn(scope, elem, attr) {
            scope.uiLoading = false;
            scope.uiErrorLoading = false;
            scope.presetSuggestions = columnFilterService.getPresetSuggestions(
                scope.filterColumn
            );
            scope.filterSuggestionsText = {
                value: ''
            };

            scope.filter = {
                value: ''
            };
            scope.selectAll = {
                value: false
            };
            scope.suggestions = [];
            scope.onSuggestionsToggle = onSuggestionsToggle;
            scope.onFilterSuggestionsTextChange = onFilterSuggestionsTextChange;
            scope.submitFilter = submitFilter;
            scope.syncSelectAllButton = syncSelectAllButton;
            scope.selectAllSuggestions = selectAllSuggestions;

            /**
             * Triggers when the dropdown for suggestions opens or closes.
             * This is only important for suggestion-based column filters.
             *
             * @memberof pkpAutocompleteTextbox
             * @param {boolean} wasOpened Whether or not the dropdown event was triggered by opening or closing.
             * @returns {void} This is just doing assignments to the column filter model.
             */
            function onSuggestionsToggle(wasOpened) {
                if (scope.presetSuggestions) {
                    $timeout(function () {
                        scope.suggestions =
                            columnFilterService.addSelectedFlags(
                                scope.currentFilter[scope.filterColumn],
                                scope.presetSuggestions
                            );
                        scope.truncated = false;
                    });
                } else if (
                    !wasOpened &&
                    !!scope.filterSuggestionsText.value &&
                    !columnFilterService.filterSelectedSuggestions(
                        scope.suggestions
                    ).length
                ) {
                    selectAllSuggestions(true);
                    submitFilter();
                }
            }

            /**
             * Requests the suggestions dynamically when the search query is at least three chars long.
             * This is for suggestion-based column filters with dynamic suggestions.
             *
             * @memberof pkpAutocompleteTextbox
             * @returns {void}
             */
            function onFilterSuggestionsTextChange() {
                if (!scope.filterSuggestionsText.value) {
                    selectAllSuggestions(false);
                    submitFilter();
                } else {
                    requestSuggestions();
                }
            }

            /**
             * Sets the label optionally and the column filter model when a suggestion is checked/unchecked or the user
             * has typed a new search query for suggestions.
             *
             * @memberof pkpAutocompleteTextbox
             * @param {boolean} [updateLabelAsWell=true] In case the input field label should also be updated. In some
             *  cases you want to prevent that when the user has just entered a search query and automatically all
             *  options are pre-selected. You would not want the search query to be overridden by the pre-selection.
             * @param {boolean} [calledWithTimeout=false] This function should be called with `$timeout` service,
             *  so that changes on the scope will be applied. If this isn't set, the function will call itself with the
             *  `$timeout` service itself, so you can basically ignore this parameter.
             * @returns {void} This is just doing assignments to the column filter model.
             */
            function submitFilter(updateLabelAsWell?, calledWithTimeout?) {
                calledWithTimeout = !!calledWithTimeout;
                updateLabelAsWell = angular.isUndefined(updateLabelAsWell)
                    ? true
                    : !!updateLabelAsWell;

                if (!calledWithTimeout) {
                    $timeout(function () {
                        submitFilter(updateLabelAsWell, true);
                    });
                }

                var selectedFilters =
                    columnFilterService.filterSelectedSuggestions(
                        scope.suggestions
                    );
                var selectedFilterValues = [];
                var selectedFilterLabels = [];

                for (var i = 0; i < selectedFilters.length; i++) {
                    selectedFilterValues.push(selectedFilters[i].value);
                    selectedFilterLabels.push(selectedFilters[i].label);
                }

                if (updateLabelAsWell) {
                    scope.filterSuggestionsText.value =
                        selectedFilterLabels.join(', ');
                }
                scope.currentFilter[scope.filterColumn] = selectedFilterValues;
            }

            /**
             * Based on the text in the column filter, suggestions to filter by are requested from the server.
             *
             * @memberof pkpAutocompleteTextbox
             * @returns {void}
             */
            function requestSuggestions() {
                scope.uiLoading = true;

                var param = {
                    filterColumn: scope.filterColumn,
                    filterTable: scope.filterTable,
                    infix: scope.filterSuggestionsText.value
                };
                angular.extend(param, scope.currentFilter);

                ColumnFilter.query(
                    param,
                    function (res) {
                        scope.suggestions = res.options;
                        scope.truncated = res.truncated;
                        selectAllSuggestions(true);
                        submitFilter(false);
                    },
                    function () {
                        scope.uiErrorLoading = true;
                    }
                ).$promise.finally(function () {
                    scope.uiLoading = false;
                });
            }

            /**
             * The "Select all" checkbox synchronizes according to the selected suggestions.
             * When all suggestions have been selected, the "select all" checkbox will also be selected and vise versa.
             *
             * @memberof pkpAutocompleteTextbox
             * @returns {void}
             */
            function syncSelectAllButton() {
                var selectedSuggestions =
                    columnFilterService.filterSelectedSuggestions(
                        scope.suggestions
                    );
                scope.selectAll.value =
                    selectedSuggestions.length === scope.suggestions.length;
            }

            /**
             * The "Select all" button selects all of the listed suggestions.
             *
             * @memberof pkpAutocompleteTextbox
             * @param {boolean} [selectAll] Defaults to the model-bound select all button, if not set right this model
             *  value will be assigned to `selectAll`.
             * @returns {boolean} `true` if all were selected, `false` otherwise.
             */
            function selectAllSuggestions(selectAll) {
                selectAll = angular.isUndefined(selectAll)
                    ? scope.selectAll.value
                    : !!selectAll;
                scope.selectAll.value = selectAll;

                angular.forEach(scope.suggestions, function (obj) {
                    obj._selected = selectAll;
                });

                return selectAll;
            }
        }
    }
})();
